import { createReducer, on } from "@ngrx/store";
import { ReducerUtilsService } from "../../../services/reducer-utils.service";
import { VigilantApiDtosUsersGetResponse } from "./users.model";

import * as UsersActions from "./users.actions";
export { UsersActions };

/**
 * Represents the state for users.
 *
 * @typedef {Object} UsersState
 * @property {VigilantApiDtosUsersGetResponse[] | undefined} users - An array of user objects or undefined.
 */
export interface UsersState {
  users: VigilantApiDtosUsersGetResponse[] | undefined;
}

/**
 * The default state for users.
 *
 * @type {UsersState}
 */
const defaultState: UsersState = {
  users: undefined,
};

/**
 * Reducer function to handle users state changes.
 *
 * This reducer manages state transitions for users through various actions:
 *
 * - **SetUsers**: Sets the users state to the provided array.
 * - **ResetUsers**: Resets the users state using the default reset logic.
 * - **AddUser**: Adds a single user to the current users state.
 * - **AddUsers**: Adds multiple users to the current users state.
 * - **UpdateUser**: Updates a single user in the users state.
 * - **UpdateUsers**: Updates multiple users in the users state.
 * - **DeleteUser**: Removes a single user by its identifier.
 * - **DeleteUsers**: Removes multiple users by their identifiers.
 *
 * @constant
 * @function usersReducer
 * @param {UsersState} state - The current state.
 * @param {Object} action - The dispatched action containing the necessary payload.
 * @returns {UsersState} The updated state.
 */
export const usersReducer = createReducer(
  defaultState,

  /**
   * Handles setting the users.
   *
   * @param {UsersState} state - The current users state.
   * @param {Object} action - The action containing the users payload.
   * @returns {UsersState} The new state with users set.
   */
  on(
    UsersActions.SetUsers,
    (state, action): UsersState => ({
      ...state,
      users: ReducerUtilsService.set(action.users),
    })
  ),

  /**
   * Handles resetting the users.
   *
   * @param {UsersState} state - The current users state.
   * @returns {UsersState} The new state with users reset.
   */
  on(
    UsersActions.ResetUsers,
    (state): UsersState => ({
      ...state,
      users: ReducerUtilsService.reset(),
    })
  ),

  /**
   * Handles adding a single user.
   *
   * @param {UsersState} state - The current users state.
   * @param {Object} action - The action containing the user to add.
   * @returns {UsersState} The new state with the user added.
   */
  on(
    UsersActions.AddUser,
    (state, action): UsersState => ({
      ...state,
      users: ReducerUtilsService.addItem(state.users, action.user),
    })
  ),

  /**
   * Handles adding multiple users.
   *
   * @param {UsersState} state - The current users state.
   * @param {Object} action - The action containing the users to add.
   * @returns {UsersState} The new state with the users added.
   */
  on(
    UsersActions.AddUsers,
    (state, action): UsersState => ({
      ...state,
      users: ReducerUtilsService.addItems(state.users, action.users),
    })
  ),

  /**
   * Handles updating a single user.
   *
   * @param {UsersState} state - The current users state.
   * @param {Object} action - The action containing the updated user.
   * @returns {UsersState} The new state with the user updated.
   */
  on(
    UsersActions.UpdateUser,
    (state, action): UsersState => ({
      ...state,
      users: ReducerUtilsService.updateItem(state.users, action.user),
    })
  ),

  /**
   * Handles updating multiple users.
   *
   * @param {UsersState} state - The current users state.
   * @param {Object} action - The action containing the users to update.
   * @returns {UsersState} The new state with the users updated.
   */
  on(
    UsersActions.UpdateUsers,
    (state, action): UsersState => ({
      ...state,
      users: ReducerUtilsService.updateItems(state.users, action.users),
    })
  ),

  /**
   * Handles deleting a single user.
   *
   * @param {UsersState} state - The current users state.
   * @param {Object} action - The action containing the id of the user to delete.
   * @returns {UsersState} The new state with the user removed.
   */
  on(
    UsersActions.DeleteUser,
    (state, action): UsersState => {

      return {
        ...state,
        users: ReducerUtilsService.deleteItem(state.users, action.id),
      };
    }
  ),

  /**
   * Handles deleting multiple users.
   *
   * @param {UsersState} state - The current users state.
   * @param {Object} action - The action containing the ids of users to delete.
   * @returns {UsersState} The new state with the specified users removed.
   */
  on(
    UsersActions.DeleteUsers,
    (state, action): UsersState => ({
      ...state,
      users: ReducerUtilsService.deleteItems(state.users, action.ids),
    })
  )
);
